import { Injector, LOCALE_ID } from '@angular/core';
import { Validators } from '@angular/forms';
import { ColumnSizes, FieldSelectOption, FilterType } from 'components';
import { DateDisplayPipe } from 'projects/components/src/lib/shared/pipes/date-display/date-display.pipe';
import { Report } from '../../models/report';
import {
  LocationsLookupService,
  AttendingPhysiciansLookupService,
  PhysiciansLookupService
} from '../../../../core/services/lookup';
import { PaymentAnalysisReportService } from './payment-analysis-report.service';

const dateFormat = new Intl.DateTimeFormat('en-us');

export const PaymentAnalysisReport: Report = {
  id: 'payment-analysis-report',
  label: 'Payment Analysis Report',
  textCriteria: {
    'Date Range Type': '',
    'Start Date': '',
    'End Date': '',
    'Payment Method': '',
    'Payment Source': '',
    'PaymentRangeLow': '',
    'PaymentRangeHigh': '',
    'Locations': '',
    'Providers': ''
  },
  cleanFilterParams: {
    'locationIds': '', 'providerIds': ''
  },
  getReportTitle: () => {
    return (
      PaymentAnalysisReport.label +
      ' ' +
      PaymentAnalysisReport.textCriteria['Start Date'] +
      ' - ' +
      PaymentAnalysisReport.textCriteria['End Date'] +
      ' (' +
      PaymentAnalysisReport.textCriteria['Date Range Type'] +
      ')'
    );
  },
  getSearchCriteria: (injector: Injector) => [
    {
      title: '',
      controls: [
        {
          label: 'Range Type',
          name: 'dateRangeType',
          type: 'select',
          class: 'form-span-6 ',
          options: [
            'Service Date',
            'Posting Date',
            'Payment Date'
          ],
          validators: Validators.required,
          selectionChanged: (event) => {
            PaymentAnalysisReport.textCriteria['Date Range Type'] = event.source.triggerValue;
          }
        },
        {
          label: 'Date Range',
          name: 'dateRange',
          type: 'dateRange',
          class: 'form-span-6',
          daterangeStartChanged: (event) => {
            PaymentAnalysisReport.textCriteria['Start Date'] = event.value.toLocaleDateString();
          },
          daterangeEndChanged: (event) => {
            PaymentAnalysisReport.textCriteria['End Date'] = event.value.toLocaleDateString();
          }
        },
        {
          label: '',
          name: 'dateRangeStart',
          type: 'noInput',
          class: 'display-none',
          validators: Validators.required
        },
        {
          label: '',
          name: 'dateRangeEnd',
          type: 'noInput',
          class: 'display-none',
          validators: Validators.required
        },
        {
          label: 'Payment Method',
          name: 'paymentMethod',
          class: 'form-span-6',
          type: 'select',
          multiple: true,
          options: [
            {
              value: 'Automatic Payment',
              label: 'ImaginePay (automatic)'
            },
            {
              value: 'ImaginePay Import',
              label: 'ImaginePay (file import)'
            },
            {
              value: 'Secure Payment',
              label: 'ImaginePay (manual)'
            },
            {
              value: 'Manual Transaction Entry',
              label: 'Manual Transaction Entry'
            },
            {
              value: 'Remittance',
              label: 'Remittance'
            },
            {
              value: 'ImaginePayUnapplied',
              label: 'ImaginePay Unapplied '
            },
          ],
          selectionChanged: (event) => {
            PaymentAnalysisReport.textCriteria['Payment Method'] = event.source.triggerValue;
          },
        },
        {
          label: 'Payment Source',
          name: 'paymentSource',
          class: 'form-span-6',
          type: 'select',
          options: ['Insurance', 'Patient'],
          selectionChanged: (event) => {
            PaymentAnalysisReport.textCriteria['Payment Source'] = event.source.triggerValue;
          }
        },
        {
          label: 'Payment Range Low End',
          name: 'paymentRangeLow',
          type: 'money',
          class: 'form-span-3'
        },
        {
          label: 'Payment Range High End',
          name: 'paymentRangeHigh',
          type: 'money',
          class: 'form-span-3'
        },
        {
          matLabel: 'Location',
          name: 'locationIds',
          apiService: injector.get(LocationsLookupService),
          type: 'multiSelect',
          class: 'form-span-6 multi-input',
          placeholder: 'Location',
          selectOptions: (event) => {
            let listValue = []
            let listText = []
            event.forEach((x: FieldSelectOption) => {

              listValue.push(x.value)
              listText.push(x.label)
            })
            PaymentAnalysisReport.textCriteria['Location'] = listText.join(',');
            PaymentAnalysisReport.cleanFilterParams['locationIds'] = listValue
          }
        },
        {
          type: 'empty',
          class: 'form-span-3'
        },
        {
          matLabel: 'Provider',
          name: 'providerIds',
          apiService: injector.get(PhysiciansLookupService),
          type: 'multiSelect',
          class: 'form-span-6 multi-input',
          placeholder: 'Provider',
          selectOptions: (event) => {
            let listValue = []
            let listText = []
            event.forEach((x: FieldSelectOption) => {
              listValue.push(x.value)
              listText.push(x.label)
            })
            PaymentAnalysisReport.textCriteria['Provider'] = listText.join(',');
            PaymentAnalysisReport.cleanFilterParams['providerIds'] = listValue

          }
        },
      ]
    }
  ],
  getGridConfiguration: (injector: Injector) => {
    const locale = injector.get(LOCALE_ID);
    const moneyFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    return {
      service: injector.get<any>(PaymentAnalysisReportService),
      columnDefinitions: [
        {
          id: 'Account Number',
          displayKey: 'Account Number',
          headerText: 'Account Number',
          displayOrder: 0,
          columnSize: ColumnSizes.normal,
          sortable: true,
          filterable: true,
          cellClickEvent: (data) => {
            if (data.patientId !== '' && data.patientId !== null) {
              window.open(`/accounts/detail/${data.patientId}`, '_blank');
            }
          },
          link_underline_condition: (data) => {
            return data.patientId !== null && data.patientId !== '';
          }
        },
        {
          id: 'Full Name',
          displayKey: 'Full Name',
          headerText: 'Full Name',
          displayOrder: 1,
          columnSize: ColumnSizes.normal,
          sortable: true,
          filterable: true,
          cellClickEvent: (data) => {
            if (data.patientId !== '' && data.patientId !== null) {
              window.open(`/accounts/detail/${data.patientId}`, '_blank');
            }
          },
          link_underline_condition: (data) => {
            return data.patientId !== null && data.patientId !== '';
          }
        },
        {
          id: 'Payment Amount',
          displayKey: 'Payment Amount',
          headerText: 'Payment Amount',
          displayOrder: 2,
          columnSize: ColumnSizes.normal,
          sortable: true,
          filterable: true,
          formatter: moneyFormat.format,
          sortColumnType: 'number',
          filterConfig: {
            filterType: FilterType.number
          }
        },
        {
          id: 'Payment Method',
          displayKey: 'Payment Method',
          headerText: 'Payment Method',
          displayOrder: 10,
          columnSize: ColumnSizes.normal,
          sortable: true,
          filterable: true
        },
        {
          id: 'Payment Source',
          displayKey: 'Payment Source',
          headerText: 'Payment Source',
          displayOrder: 3,
          columnSize: ColumnSizes.narrow,
          sortable: true,
          filterable: true
        },
        {
          id: 'Batch Number',
          displayKey: 'Batch Number',
          headerText: 'Batch Number',
          displayOrder: 5,
          columnSize: ColumnSizes.normal,
          sortable: true,
          filterable: true,
          cellClickEvent: (data) => {
            if (data['Batch Number'] !== '' && data['Batch Number'] !== null) {
              sessionStorage.setItem('batchNumber', data['Batch Number']);
              sessionStorage.setItem('batchId', data.batchId);
              window.open(`/transactions/batches/${data['Batch Number']}`, '_blank');
            }
          },
          link_underline_condition: (data) => {
            return data.Batch !== null && data.Batch !== '';
          },
          sortColumnType: 'number',
          filterConfig: {
            filterType: FilterType.number
          }
        },
        {
          id: 'Payment Date',
          displayKey: 'Payment Date',
          headerText: 'Payment Date',
          displayOrder: 11,
          columnSize: ColumnSizes.date,
          sortable: true,
          filterable: true,
          filterConfig: {
            filterType: FilterType.date,
            filterKey: 'Payment Date'
          },
          sortColumnType: 'date',
        },
        {
          id: 'Service Date',
          displayKey: 'Service Date',
          headerText: 'Service Date',
          displayOrder: 6,
          columnSize: ColumnSizes.date,
          sortable: true,
          filterable: true,
          filterConfig: {
            filterType: FilterType.date,
            filterKey: 'Service Date'
          },
          sortColumnType: 'date',
        },
        {
          id: 'Provider',
          displayKey: 'Provider',
          headerText: 'Provider',
          displayOrder: 7,
          columnSize: ColumnSizes.normal,
          sortable: true,
          filterable: true
        },
        {
          id: 'Location',
          displayKey: 'Location',
          headerText: 'Location',
          displayOrder: 8,
          columnSize: ColumnSizes.normal,
          sortable: true,
          filterable: true
        },
        {
          id: 'Check Number',
          displayKey: 'Check Number',
          headerText: 'Check Number',
          displayOrder: 9,
          columnSize: ColumnSizes.normal,
          sortable: true,
          filterable: true
        },
        {
          id: 'Posting Date',
          displayKey: 'Posting Date',
          headerText: 'Posting Date',
          displayOrder: 12,
          columnSize: ColumnSizes.date,
          sortable: true,
          filterable: true,
          filterConfig: {
            filterType: FilterType.date,
            filterKey: 'Posting Date'
          },
          sortColumnType: 'date',
        },
        {
          id: 'ICN',
          displayKey: 'ICN',
          headerText: 'ICN',
          displayOrder: 10,
          columnSize: ColumnSizes.normal,
          sortable: true,
          filterable: true
        },
      ]
    }
  }
};
